import { useState } from "react";
import { motion } from "framer-motion";
import { Linkedin } from "lucide-react";
import { PARTNERSHIP_TYPES } from "@/data/content";
import { FOUNDER } from "@/data/site";
import { SectionHead } from "@/components/Blocks";
import { Cta } from "@/components/Cta";
import { Reveal, EASE } from "@/components/motion";
import { Sparkle, Ribbons } from "@/components/Art";

const EXAMPLE = [
  ["Problem", "A founder has validated demand for a B2B workflow tool through spreadsheets and calls, but has no product and no technical co-founder."],
  ["Constraints", "Limited runway, a fixed investor demo date, and a need for the first version to handle real customer data safely."],
  ["Approach", "A short discovery phase to cut scope to one core workflow, a design prototype tested with five target users, then two-week build cycles with working demos."],
  ["Delivered", "A production web app with authentication, the core workflow, an admin view, payments, and monitoring — plus documentation the founder owns."],
  ["Result", "In a real engagement, this is where we'd report verified outcomes. We only publish results we can evidence."],
];

export function Approach() {
  const [tab, setTab] = useState(0);
  return (
    <section className="relative border-t border-si-line bg-si-bg2 py-24 sm:py-32" data-testid="approach-section">
      <div className="wrap grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <SectionHead eyebrow="How we approach product engineering" title={["Ideas are only the beginning.", "Execution matters."]}
            intro="Our clearest proof is the products we build ourselves. For client work, every case study follows the same structure — shown here with an illustrative example." />
          <Reveal delay={0.2} className="mt-10 flex flex-wrap gap-3">
            <Cta to="/work" variant="secondary" testId="approach-cta-work">Explore Our Work</Cta>
          </Reveal>
        </div>
        <Reveal delay={0.1} className="lg:col-span-7">
          <div className="overflow-hidden rounded-3xl border border-si-line bg-si-surface/60">
            <div className="flex items-center justify-between border-b border-si-line px-6 py-4">
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-si-gold">Illustrative example</span>
              <span className="font-mono text-xs text-si-muted">Not a client case study</span>
            </div>
            <div className="flex overflow-x-auto border-b border-si-line" role="tablist" aria-label="Case study structure" data-lenis-prevent>
              {EXAMPLE.map(([t], i) => (
                <button key={t} role="tab" aria-selected={tab === i} onClick={() => setTab(i)} data-testid={`approach-tab-${i}`}
                  className={`relative shrink-0 px-5 py-4 text-sm font-semibold transition-colors ${tab === i ? "text-si-text" : "text-si-muted hover:text-si-text"}`}>
                  <span className="mr-2 font-mono text-[11px] text-si-cyan">{i + 1}</span>{t}
                  {tab === i && <motion.span layoutId="approach-ul" className="absolute inset-x-3 bottom-0 h-[2px] bg-si-cyan" />}
                </button>
              ))}
            </div>
            <div className="relative min-h-[220px] p-6 sm:p-10" role="tabpanel">
              <motion.div key={tab} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, ease: EASE }}>
                <p className="eyebrow">{EXAMPLE[tab][0]}</p>
                <p className="mt-4 text-xl font-medium leading-relaxed text-si-text sm:text-2xl">{EXAMPLE[tab][1]}</p>
              </motion.div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function PartnershipBlock() {
  return (
    <section className="relative overflow-hidden py-24 sm:py-32" data-testid="partnership-section">
      <Ribbons id="pb" className="pointer-events-none absolute inset-0 h-full w-full opacity-40" />
      <div className="wrap relative grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <SectionHead eyebrow="Venture partnerships" title={["Have an idea worth", "building together?"]}
            intro="We are interested in meeting founders who see an important problem, have a compelling vision, and want a committed technology partner to help build the solution." />
          <Reveal delay={0.2} className="mt-10"><Cta to="/venture-partnerships" size="lg" testId="partnership-cta" trackId="home-pitch-idea">Pitch Your Idea</Cta></Reveal>
        </div>
        <div className="lg:col-span-6">
          <ul className="divide-y divide-si-line border-y border-si-line">
            {PARTNERSHIP_TYPES.map((t, i) => (
              <motion.li key={t} initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.3 }}
                transition={{ delay: i * 0.08, duration: 0.6, ease: EASE }} className="group flex items-center justify-between py-5">
                <span className="text-xl font-semibold tracking-tight transition-transform duration-300 group-hover:translate-x-2 sm:text-2xl">{t}</span>
                <Sparkle className="h-4 w-4 opacity-40 transition-opacity group-hover:opacity-100" color={i === 3 ? "#F98A17" : "#17B6F4"} />
              </motion.li>
            ))}
          </ul>
          <Reveal delay={0.3} className="mt-6 rounded-2xl border border-si-orange/30 bg-si-orange/5 p-5 text-sm leading-relaxed text-[#ffd9b0]">
            Partnerships are selective and subject to evaluation. Submitting an idea does not guarantee funding, equity arrangements, or a partnership.
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export function FounderIntro() {
  return (
    <section className="relative border-t border-si-line py-24 sm:py-32" data-testid="founder-section">
      <div className="wrap grid gap-14 lg:grid-cols-12 lg:items-center">
        <Reveal className="lg:col-span-5">
          <div className="relative mx-auto aspect-[4/5] w-full max-w-[420px] overflow-hidden rounded-[28px] border border-si-line bg-[#071027] [clip-path:polygon(0_0,100%_0,100%_88%,86%_100%,0_100%)]">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_35%,rgba(23,107,255,0.45),transparent_60%)]" />
            <div className="bg-grid absolute inset-0 opacity-70" />
            <div className="absolute inset-0 grid place-items-center">
              <span className="text-[9rem] font-extrabold leading-none tracking-[-0.06em] text-cool">BS</span>
            </div>
            <img src="/brand/emblem-256.png" alt="" aria-hidden="true" className="float-slow absolute right-6 top-6 h-16 w-16" />
            <div className="absolute inset-x-0 bottom-0 p-6">
              <p className="text-2xl font-bold">{FOUNDER.name}</p>
              <p className="font-mono text-xs uppercase tracking-[0.18em] text-si-muted">{FOUNDER.role}</p>
            </div>
          </div>
        </Reveal>
        <div className="lg:col-span-7">
          <SectionHead eyebrow="Founder story" title={["Built by people who understand", "the work behind the idea."]} />
          <Reveal delay={0.15} className="mt-8 space-y-5 text-base leading-relaxed text-si-muted sm:text-lg">
            <p>{FOUNDER.bio}</p>
            <p>SparkIdea Interactive exists to do both: build products of our own, and bring the same hands-on engineering and practical product judgment to founders working within real startup constraints.</p>
          </Reveal>
          <Reveal delay={0.25} className="mt-10 flex flex-wrap items-center gap-5">
            <Cta to="/about" testId="founder-cta">Meet the Founder</Cta>
            <a href={FOUNDER.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[15px] font-semibold text-si-muted hover:text-si-cyan" data-testid="founder-linkedin">
              <Linkedin className="h-4 w-4" />LinkedIn
            </a>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
